import { memo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}

export const TablePagination = memo(function TablePagination({
  currentPage,
  totalPages,
  loading,
  onPageChange,
}: TablePaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
      <div className="text-sm text-muted-foreground">
        Página {currentPage + 1} de {totalPages}
      </div>
      <div className="flex gap-2 items-center flex-wrap justify-center">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0 || loading}
        >
          <ChevronLeft className="w-4 h-4" />
          Anterior
        </Button>

        <div className="flex gap-1 flex-wrap justify-center">
          {Array.from({ length: totalPages }, (_, i) => {
            const showPage =
              i === 0 ||
              i === totalPages - 1 ||
              (i >= currentPage - 1 && i <= currentPage + 1);

            const showEllipsis =
              (i === 1 && currentPage > 2) ||
              (i === totalPages - 2 && currentPage < totalPages - 3);

            if (showEllipsis) {
              return (
                <span
                  key={i}
                  className="px-2 py-1 text-sm text-muted-foreground"
                >
                  ...
                </span>
              );
            }

            if (!showPage) return null;

            return (
              <Button
                key={i}
                variant={currentPage === i ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(i)}
                disabled={loading}
                className="min-w-10"
              >
                {i + 1}
              </Button>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages - 1 || loading}
        >
          Próxima
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
});
